import * as Sentry from "@sentry/react";
import type { ErrorEvent } from "@sentry/react";
import { operationalRouteTemplate } from "../analytics/operationalRuntime";
import { resolveSurfaceTelemetry } from "../next/app/analytics/surfaceTelemetryRegistry";

export { resolveSentryRelease } from "./sentryRelease";

export type SentryLike = {
  init: (options: Record<string, unknown>) => void;
  setTag: (key: string, value: string) => void;
  captureException?: (error: Error, context?: Record<string, unknown>) => void;
};

// Vite replaces `import.meta.env.VITE_SENTRY_DSN` at build time. Keep every
// default-SDK use behind this value so DSN-unset production builds tree-shake
// `@sentry/react` out of the initial graph (bundle budget).
const PRODUCT_SENTRY_DSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim();
const PRODUCT_SENTRY_RELEASE = typeof __PINPOINT_SENTRY_RELEASE__ === "string"
  ? __PINPOINT_SENTRY_RELEASE__
  : undefined;

export function productSentryRelease(
  env: Record<string, unknown> = import.meta.env,
): string | undefined {
  const release = env.VITE_SENTRY_RELEASE;
  return typeof release === "string" && release.trim() ? release.trim() : PRODUCT_SENTRY_RELEASE;
}

export function productTrafficClass(
  browser: { webdriver?: boolean } | undefined =
    typeof navigator === "undefined" ? undefined : navigator,
): "synthetic" | "student" {
  return browser?.webdriver === true ? "synthetic" : "student";
}

/**
 * Classify only a verified, server-returned account. Browser automation is
 * useful probe metadata, but cannot establish student membership.
 */
export function authenticatedTrafficClass(
  user: { traffic_class?: unknown } | null | undefined,
): "student" | "synthetic" | "unknown" {
  if (!user) return "unknown";
  return user.traffic_class === "student" || user.traffic_class === "synthetic"
    ? user.traffic_class
    : "unknown";
}

export function applyAuthenticatedSentryContext(
  user: { traffic_class?: unknown } | null | undefined,
  sdk?: SentryLike,
): void {
  if (sdk) {
    sdk.setTag("traffic_class", authenticatedTrafficClass(user));
    return;
  }
  if (!PRODUCT_SENTRY_DSN) {
    return;
  }
  Sentry.setTag("traffic_class", authenticatedTrafficClass(user));
}

// Letter slugs such as course-a are still ids, so replace every segment.
function templateLearnResourcePath(value: string): string {
  return value
    .replace(/\/learn\/courses\/[^/?#]+/g, "/learn/courses/:courseId")
    .replace(/\/units\/[^/?#]+/g, "/units/:unitId")
    .replace(/\/documents\/[^/?#]+/g, "/documents/:documentId");
}

function redactLearnBreadcrumbPaths(event: ErrorEvent): void {
  for (const breadcrumb of event.breadcrumbs ?? []) {
    const data = breadcrumb?.data;
    if (!data) continue;
    for (const key of ["url", "from", "to"] as const) {
      const value = data[key];
      if (typeof value === "string") {
        data[key] = templateLearnResourcePath(value);
      }
    }
  }
}

export function redactSentryEvent(event: ErrorEvent): ErrorEvent | null {
  if (event.request?.url) {
    try {
      const parsed = new URL(event.request.url);
      parsed.search = "";
      event.request.url = templateLearnResourcePath(parsed.toString());
    } catch {
      event.request.url = "[REDACTED]";
    }
  }
  if (event.request?.headers) {
    for (const key of Object.keys(event.request.headers)) {
      if (/authorization|cookie|token/i.test(key)) {
        event.request.headers[key] = "[REDACTED]";
      }
    }
  }
  redactLearnBreadcrumbPaths(event);
  return event;
}

export function productSentryDsn(
  env: Record<string, unknown> = import.meta.env,
): string {
  return String(env.VITE_SENTRY_DSN ?? "").trim();
}

function sentryInitOptions(env: Record<string, unknown>): Record<string, unknown> {
  return {
    dsn: productSentryDsn(env),
    environment: env.MODE ?? env.VITE_ISEE_ENV,
    release: productSentryRelease(env),
    tracesSampleRate: Number(env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0.1"),
    sendDefaultPii: false,
    beforeSend: redactSentryEvent,
    initialScope: {
      tags: {
        surface: "product",
        // Authentication is the only membership authority. The historical
        // webdriver heuristic remains available for probe diagnostics but
        // cannot label pre-auth browser traffic as student.
        traffic_class: "unknown",
      },
    },
  };
}

export function initProductSentry(options?: {
  sentry?: SentryLike;
  env?: Record<string, unknown>;
}): boolean {
  if (options?.sentry) {
    const env = options.env ?? import.meta.env;
    const dsn = productSentryDsn(env);
    if (!dsn) {
      return false;
    }
    options.sentry.init(sentryInitOptions(env));
    return true;
  }
  if (!PRODUCT_SENTRY_DSN) {
    return false;
  }
  Sentry.init({
    ...sentryInitOptions({
      ...import.meta.env,
      VITE_SENTRY_RELEASE: PRODUCT_SENTRY_RELEASE,
    }),
  });
  return true;
}

function tagRoute(pathname: string, search: string, sdk: SentryLike): void {
  const entry = resolveSurfaceTelemetry(pathname, search);
  const routeTemplate = pathname.startsWith("/learn")
    ? templateLearnResourcePath(pathname)
    : operationalRouteTemplate(pathname);
  sdk.setTag("route_template", routeTemplate);
  sdk.setTag("surface_key", entry?.surfaceKey ?? "");
}

export function applySentryRouteContext(
  pathname: string,
  search = "",
  sdk?: SentryLike,
): void {
  if (sdk) {
    tagRoute(pathname, search, sdk);
    return;
  }
  if (!PRODUCT_SENTRY_DSN) {
    return;
  }
  tagRoute(pathname, search, Sentry);
}

export function captureProductException(
  error: Error,
  context?: Record<string, unknown>,
): void {
  if (!PRODUCT_SENTRY_DSN) {
    return;
  }
  Sentry.captureException(error, context);
}
